import apiClient from '../utils/api/ApiClient';
import { API_ENDPOINTS } from '../constants/api.constants';
import axios from 'axios';

export interface UploadFileRequest {
    fileName: string;
    fileType: string;
}

export interface UploadUrlResponse {
    fileName: string;
    uploadUrl: string;
    cloudFrontUrl: string;
    key: string;
}

export interface GetUploadUrlsRequest {
    storeBrandID: string;
    storeID?: string;
    section: 'logo' | 'ogImage' | 'hero' | 'gallery' | 'modal';
    files: UploadFileRequest[];
}

export interface GetUploadUrlsResponse {
    files: UploadUrlResponse[];
    expiresIn: number;
}

/**
 * Image Upload Service
 * Handles the complete flow of uploading images to S3 via pre-signed URLs
 */
export class ImageUploadService {
    /**
     * Get pre-signed upload URLs from backend
     */
    static async getUploadUrls(request: GetUploadUrlsRequest): Promise<UploadUrlResponse[]> {
        try {
            const response = await apiClient.post<{ data: GetUploadUrlsResponse }>(
                API_ENDPOINTS.MICROSITE.UPLOAD_URLS,
                request,
                { headers: { 'X-Store-Brand-Id': request.storeBrandID } }
            );
            // Backend wraps the response in a data property
            return response.data.files;
        } catch (error) {
            console.error('Error getting upload URLs:', error);
            throw new Error('Failed to get upload URLs');
        }
    }

    /**
     * Upload a single file directly to S3 using pre-signed URL
     */
    static async uploadToS3(file: File, uploadUrl: string): Promise<void> {
        try {
            await axios.put(uploadUrl, file, {
                headers: {
                    'Content-Type': file.type,
                },
            });
        } catch (error) {
            console.error('Error uploading to S3:', error);
            throw new Error(`Failed to upload ${file.name}`);
        }
    }

    /**
     * Upload multiple files for a section
     * Returns the CloudFront URLs to be used in the save API
     */
    static async uploadFilesForSection(
        files: File[],
        section: string,
        storeBrandID: string,
        storeID?: string
    ): Promise<string[]> {
        if (files.length === 0) return [];

        // Prepare file metadata
        const fileRequests: UploadFileRequest[] = files.map((file) => ({
            fileName: file.name,
            fileType: file.type,
        }));

        // Get upload URLs
        const uploadUrls = await this.getUploadUrls({
            storeBrandID,
            storeID,
            section: section as any,
            files: fileRequests,
        });

        // Upload all files to S3
        const uploadPromises = files.map((file, index) =>
            this.uploadToS3(file, uploadUrls[index].uploadUrl)
        );

        await Promise.all(uploadPromises);

        // Return CloudFront URLs
        return uploadUrls.map((url) => url.cloudFrontUrl);
    }

    /**
     * Helper: Upload a single file and return its CloudFront URL
     */
    static async uploadSingleFile(
        file: File,
        section: string,
        storeBrandID: string,
        storeID?: string
    ): Promise<string> {
        const urls = await this.uploadFilesForSection([file], section, storeBrandID, storeID);
        return urls[0];
    }
}

export default ImageUploadService;
